"use client"

import * as React from "react"
import {
  FolderOpen,
  Plus,
  Settings,
  Archive,
  ArchiveRestore,
  X,
  Pencil,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  Layers,
  Inbox,
  RefreshCw,
  Search,
  UserPlus,
  ShieldCheck,
  ShieldAlert,
  Trash2,
  Copy,
  Check,
  Users
} from "lucide-react"
import { TabsContent } from "@/components/ui/tabs"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Account, UserProfile, Workspace } from "@/types/workspace"
import { WorkspaceMembersManager } from "@/components/settings/tabs/WorkspaceMembersManager"
import { toast } from "@/lib/toast"

function generateRandomCode() {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789"
  let code = ""
  for (let i = 0; i < 4; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

export interface WorkspacesSettingsTabProps {
  currentAccount: Account | null
  handleCreateWorkspace: (name: string, accountId: string, slug?: string) => Promise<any>
  handleUpdateWorkspace?: (id: string, name: string) => Promise<any>
  handleArchiveWorkspace?: (id: string) => Promise<any>
  handleReactivateWorkspace?: (id: string) => Promise<any>
  handleDeleteWorkspace?: (id: string) => Promise<any>
  usersList: UserProfile[]
  t: Record<string, string>
  profileRole?: string
  user?: UserProfile | null
}

export function WorkspacesSettingsTab({
  currentAccount,
  handleCreateWorkspace,
  handleUpdateWorkspace,
  handleArchiveWorkspace,
  handleReactivateWorkspace,
  handleDeleteWorkspace,
  usersList,
  t,
  profileRole = "member",
  user,
}: WorkspacesSettingsTabProps) {
  const isCompanyAdmin = profileRole === "owner" || profileRole === "admin"
  const [filterTab, setFilterTab] = React.useState<"all" | "active" | "archived">("active")
  const [newWsName, setNewWsName] = React.useState("")
  const [randomCode, setRandomCode] = React.useState(() => generateRandomCode())
  const [showCreateWorkspaceModal, setShowCreateWorkspaceModal] = React.useState(false)
  const [isSubmittingWorkspace, setIsSubmittingWorkspace] = React.useState(false)
  const [editingWorkspaceModal, setEditingWorkspaceModal] = React.useState<Workspace | null>(null)
  const [editWorkspaceNameInput, setEditWorkspaceNameInput] = React.useState("")
  const [userWorkspaceRoles, setUserWorkspaceRoles] = React.useState<Record<string, string>>({})
  const [workspaceToArchive, setWorkspaceToArchive] = React.useState<Workspace | null>(null)
  const [isArchivingWorkspace, setIsArchivingWorkspace] = React.useState(false)
  const [workspaceToReactivate, setWorkspaceToReactivate] = React.useState<Workspace | null>(null)
  const [isReactivatingWorkspace, setIsReactivatingWorkspace] = React.useState(false)

  // Membros iniciais ao criar novo ambiente
  const [initialMembers, setInitialMembers] = React.useState<Array<{ userId: string; role: string }>>([])
  const [selectedUserToAdd, setSelectedUserToAdd] = React.useState<string>("")
  const [selectedRoleToAdd, setSelectedRoleToAdd] = React.useState<string>("member")
  const [searchMemberQuery, setSearchMemberQuery] = React.useState<string>("")
  const [copiedSlug, setCopiedSlug] = React.useState<boolean>(false)

  // Função auxiliar para inicializar os membros da criação com os Admins e Owner da Empresa
  const getInitialAdminMembers = React.useCallback(() => {
    return usersList
      .filter((u) => {
        const role = (u as any)?.role
        const status = (u as any)?.status
        return status !== "pending" && (role === "owner" || role === "admin")
      })
      .map((u) => ({
        userId: u.id,
        role: "admin"
      }))
  }, [usersList])

  // Resolvers para arquivar e reativar (com fallback para handleDeleteWorkspace se handleArchiveWorkspace não for passado)
  const archiveAction = handleArchiveWorkspace || handleDeleteWorkspace
  const reactivateAction = handleReactivateWorkspace

  // Buscar papéis do usuário nos ambientes caso ele seja membro comum da empresa
  React.useEffect(() => {
    if (isCompanyAdmin) return
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    const userId = user?.id || (user as any)?.userId
    if (!token || !userId) return

    fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          query GetUserWorkspaceMemberships($userId: ID!) {
            userWorkspaceMemberships(userId: $userId) {
              workspaceId
              role
            }
          }
        `,
        variables: { userId }
      })
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.data?.userWorkspaceMemberships) {
          const map: Record<string, string> = {}
          res.data.userWorkspaceMemberships.forEach((m: any) => {
            map[m.workspaceId] = m.role
          })
          setUserWorkspaceRoles(map)
        }
      })
      .catch((e) => console.error("Erro ao buscar papéis nos ambientes:", e))
  }, [user?.id, (user as any)?.userId, isCompanyAdmin])

  const allWorkspaces = currentAccount?.workspaces || []
  const activeWorkspaces = allWorkspaces.filter((w) => w.status !== false)
  const archivedWorkspaces = allWorkspaces.filter((w) => w.status === false)
  const canArchiveMore = activeWorkspaces.length > 1

  const baseSlug = React.useMemo(() => {
    const cleaned = newWsName
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
    return cleaned || "ambiente"
  }, [newWsName])

  const generatedSlug = `${baseSlug}-${randomCode}`

  const isSlugTaken = React.useMemo(() => {
    return allWorkspaces.some((w) => w.slug?.toLowerCase() === generatedSlug.toLowerCase())
  }, [allWorkspaces, generatedSlug])

  React.useEffect(() => {
    if (isSlugTaken) {
      setRandomCode(generateRandomCode())
    }
  }, [isSlugTaken])

  const displayedWorkspaces = filterTab === "active"
    ? activeWorkspaces
    : filterTab === "archived"
      ? archivedWorkspaces
      : allWorkspaces

  return (
    <TabsContent value="workspaces" className="outline-none space-y-6">
      {/* CABEÇALHO */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
            <FolderOpen className="size-5 text-[#004EB1]" />
            Gerenciar Ambientes
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
            {isCompanyAdmin
              ? "Crie, arquive e gerencie os ambientes de trabalho da sua empresa, configure membros e permissões."
              : "Visualize e gerencie os ambientes de trabalho aos quais você tem acesso nesta empresa."}
          </p>
        </div>

        {isCompanyAdmin && (
          <Button
            type="button"
            onClick={() => {
              setNewWsName("")
              setRandomCode(generateRandomCode())
              setInitialMembers(getInitialAdminMembers())
              setSelectedUserToAdd("")
              setShowCreateWorkspaceModal(true)
            }}
            className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 gap-1.5 cursor-pointer shadow-sm shrink-0"
          >
            <Plus className="size-4" />
            Criar Ambiente
          </Button>
        )}
      </div>

      {/* FILTRO DE ABAS (TODOS / ATIVOS / ARQUIVADOS) */}
      {isCompanyAdmin && (
        <div className="flex items-center gap-1.5 border-b border-zinc-200 dark:border-zinc-800 pb-2">
          <button
            type="button"
            onClick={() => setFilterTab("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${filterTab === "all"
              ? "bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-xs"
              : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
              }`}
          >
            <Layers className="size-3.5" />
            Todos
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${filterTab === "all"
              ? "bg-white/20 dark:bg-black/20 text-white dark:text-zinc-900"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
              }`}>
              {allWorkspaces.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setFilterTab("active")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${filterTab === "active"
              ? "bg-emerald-600 text-white shadow-xs"
              : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
              }`}
          >
            <span className="size-2 rounded-full bg-emerald-400"></span>
            Ativos
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${filterTab === "active"
              ? "bg-white/20 text-white"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
              }`}>
              {activeWorkspaces.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setFilterTab("archived")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${filterTab === "archived"
              ? "bg-amber-600 text-white shadow-xs"
              : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
              }`}
          >
            <Archive className="size-3.5" />
            Arquivados
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${filterTab === "archived"
              ? "bg-white/20 text-white"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
              }`}>
              {archivedWorkspaces.length}
            </span>
          </button>
        </div>
      )}

      {/* ESTADO VAZIO QUANDO FILTRADO */}
      {displayedWorkspaces.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 text-center border border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl bg-zinc-50/50 dark:bg-zinc-900/30">
          <div className="size-12 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 mb-3">
            <Inbox className="size-6" />
          </div>
          <h4 className="text-sm font-bold text-zinc-700 dark:text-zinc-300">
            {filterTab === "archived" ? "Nenhum ambiente arquivado" : "Nenhum ambiente encontrado"}
          </h4>
          <p className="text-xs text-zinc-400 mt-1 max-w-sm">
            {filterTab === "archived"
              ? "Ambientes arquivados serão exibidos nesta seção quando você os arquivar."
              : "Não há ambientes disponíveis com os filtros selecionados."}
          </p>
        </div>
      ) : (
        /* LISTA DE AMBIENTES EM CARDS */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayedWorkspaces.map((ws) => {
            const isWorkspaceAdmin = isCompanyAdmin || userWorkspaceRoles[ws.id] === "admin"
            const isArchived = ws.status === false

            return (
              <Card
                key={ws.id}
                className={`border transition-all shadow-xs ${isArchived
                  ? "border-amber-200/60 dark:border-amber-900/30 bg-amber-50/20 dark:bg-amber-950/10 opacity-85 hover:opacity-100"
                  : "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-zinc-300 dark:hover:border-zinc-700"
                  }`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`size-10 rounded-xl flex items-center justify-center font-bold text-base border ${isArchived
                          ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border-zinc-200 dark:border-zinc-700"
                          : "bg-blue-50 dark:bg-blue-950/40 text-[#004EB1] dark:text-blue-400 border-blue-100 dark:border-blue-900/50"
                          }`}
                      >
                        {ws.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white">
                            {ws.name}
                          </CardTitle>
                          {isArchived ? (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50 flex items-center gap-1">
                              <Archive className="size-3" />
                              Arquivado
                            </span>
                          ) : (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/50 flex items-center gap-1">
                              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                              Ativo
                            </span>
                          )}
                        </div>
                        <CardDescription className="text-[11px] text-zinc-400 font-mono mt-0.5">
                          {ws.slug}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-100 dark:border-zinc-800 mt-2">
                    <div className="text-[11px] text-zinc-400 font-medium">
                      {isArchived ? "Ambiente Arquivado" : "Ambiente Corporativo"}
                    </div>

                    {isWorkspaceAdmin ? (
                      <div className="flex items-center gap-2">
                        {!isArchived && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingWorkspaceModal(ws)
                              setEditWorkspaceNameInput(ws.name)
                            }}
                            className="h-8 text-xs font-semibold text-[#004EB1] border-blue-200 dark:border-blue-900 hover:bg-blue-50 dark:hover:bg-blue-950/30 gap-1.5 cursor-pointer"
                          >
                            <Settings className="size-3.5" />
                            Configurar
                          </Button>
                        )}

                        {isCompanyAdmin && (
                          <>
                            {isArchived ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setWorkspaceToReactivate(ws)}
                                className="h-8 text-xs font-semibold text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 gap-1.5 cursor-pointer"
                                title="Reativar Ambiente"
                              >
                                <ArchiveRestore className="size-3.5" />
                              </Button>
                            ) : (
                              archiveAction && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={!canArchiveMore}
                                  onClick={() => setWorkspaceToArchive(ws)}
                                  className={`h-8 px-2.5 gap-1.5 text-xs font-semibold transition-colors cursor-pointer ${canArchiveMore
                                    ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                                    : "text-zinc-300 dark:text-zinc-600 cursor-not-allowed"
                                    }`}
                                  title={
                                    canArchiveMore
                                      ? "Arquivar Ambiente"
                                      : "A empresa deve possuir pelo menos um ambiente ativo."
                                  }
                                >
                                  <Archive className="size-3.5" />
                                </Button>
                              )
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 rounded-md">
                        Acesso Concedido
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* MODAL DE CRIAÇÃO DE AMBIENTE */}
      {showCreateWorkspaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="w-full max-w-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden font-sans animate-in fade-in zoom-in-95 duration-150 my-6">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-[#004EB1] flex items-center justify-center">
                  <Plus className="size-4.5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Criar Novo Ambiente</h4>
                  <p className="text-[11px] text-zinc-500">Configure as informações e os membros iniciais do ambiente</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowCreateWorkspaceModal(false)
                  setInitialMembers([])
                  setSelectedUserToAdd("")
                }}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (!newWsName.trim() || !currentAccount) return
                setIsSubmittingWorkspace(true)
                try {
                  const newWs = await handleCreateWorkspace(newWsName.trim(), currentAccount.id, generatedSlug)
                  if (currentAccount) {
                    currentAccount.workspaces = [...(currentAccount.workspaces || []), newWs]
                  }

                  // Adicionar membros selecionados ao novo ambiente
                  if (initialMembers.length > 0 && newWs?.id) {
                    const token = document.cookie
                      .split("; ")
                      .find((row) => row.startsWith("token="))
                      ?.split("=")[1]
                    if (token) {
                      await Promise.all(
                        initialMembers.map((m) =>
                          fetch("/api/graphql", {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              query: `
                                mutation AddWorkspaceMember($workspaceId: ID!, $userId: ID!, $role: String) {
                                  addWorkspaceMember(workspaceId: $workspaceId, userId: $userId, role: $role)
                                }
                              `,
                              variables: {
                                workspaceId: newWs.id,
                                userId: m.userId,
                                role: m.role
                              }
                            })
                          }).catch((err) => console.error("Erro ao adicionar membro inicial:", err))
                        )
                      )
                    }
                  }

                  toast.success(`Ambiente "${newWs.name}" criado com sucesso!`)
                  setNewWsName("")
                  setInitialMembers([])
                  setSelectedUserToAdd("")
                  setRandomCode(generateRandomCode())
                  setShowCreateWorkspaceModal(false)
                } catch (err: any) {
                  toast.error(err.message || "Erro ao criar ambiente.")
                } finally {
                  setIsSubmittingWorkspace(false)
                }
              }}
              className="p-6 space-y-5 max-h-[80vh] overflow-y-auto"
            >
              {/* Informações Básicas */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                    Nome do Ambiente <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={newWsName}
                    onChange={(e) => setNewWsName(e.target.value)}
                    placeholder="Meu Ambiente"
                    className="w-full rounded-xl border border-zinc-200 dark:border-zinc-800 px-3.5 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300 flex items-center justify-between">
                    <span>Identificador Único (Slug)</span>
                    <span className="text-[10px] font-normal text-zinc-400">Gerado automaticamente</span>
                  </label>
                  <div className="relative flex items-center">
                    <span className="absolute left-3.5 text-zinc-400 dark:text-zinc-500 text-xs font-mono select-none">
                      @
                    </span>
                    <input
                      type="text"
                      readOnly
                      value={generatedSlug}
                      className="w-full rounded-xl border border-zinc-200 dark:border-zinc-800 pl-8 pr-9 py-2 text-xs font-mono bg-zinc-50 dark:bg-zinc-950/60 text-zinc-600 dark:text-zinc-400 cursor-default outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setRandomCode(generateRandomCode())}
                      title="Gerar novo código único"
                      className="absolute right-2 p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-200/50 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
                    >
                      <RefreshCw className="size-3.5" />
                    </button>
                  </div>
                  <p className="text-[10px] text-zinc-400">
                    O sufixo aleatório garante que nenhum ambiente tenha o mesmo identificador na URL.
                  </p>
                </div>
              </div>

              {/* Seção Membros Iniciais */}
              <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-xs font-bold text-zinc-900 dark:text-white flex items-center gap-1.5">
                      <Users className="size-3.5 text-[#004EB1]" />
                      Adicionar Membros ao Ambiente
                    </h5>
                    <p className="text-[11px] text-zinc-500">
                      Defina quem terá acesso e qual papel exercerá neste ambiente
                    </p>
                  </div>
                  <span className="text-[10px] bg-blue-50 dark:bg-blue-950/50 text-[#004EB1] font-semibold px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-900/60">
                    {initialMembers.length} {initialMembers.length === 1 ? "selecionado" : "selecionados"}
                  </span>
                </div>

                {/* Seletores para adicionar novo membro */}
                <div className="flex flex-col sm:flex-row items-stretch gap-2 bg-zinc-50/80 dark:bg-zinc-950/80 p-2.5 rounded-xl border border-zinc-200/80 dark:border-zinc-800">
                  <div className="relative flex-1">
                    <select
                      value={selectedUserToAdd}
                      onChange={(e) => setSelectedUserToAdd(e.target.value)}
                      className="w-full appearance-none rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-xs bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white focus:border-[#004EB1] outline-none pr-8 cursor-pointer"
                    >
                      <option value="">Selecione os usuários...</option>
                      {usersList
                        .filter(
                          (u) =>
                            (u as any).status !== "pending" &&
                            !initialMembers.some((m) => m.userId === u.id)
                        )
                        .map((u) => {
                          const isCorpAdmin = (u as any).role === "owner" || (u as any).role === "admin"
                          return (
                            <option key={u.id} value={u.id}>
                              {u.name || u.email} ({u.email}) {isCorpAdmin ? "• Admin Empresa" : ""}
                            </option>
                          )
                        })}
                    </select>
                  </div>

                  <select
                    value={selectedRoleToAdd}
                    onChange={(e) => setSelectedRoleToAdd(e.target.value)}
                    className="w-32 rounded-lg border border-zinc-200 dark:border-zinc-800 px-2.5 py-2 text-xs bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white focus:border-[#004EB1] outline-none cursor-pointer"
                  >
                    <option value="member">Membro</option>
                    <option value="manager">Gerente</option>
                    <option value="admin">Administrador</option>
                  </select>

                  <Button
                    type="button"
                    disabled={!selectedUserToAdd}
                    onClick={() => {
                      if (!selectedUserToAdd) return
                      setInitialMembers([
                        ...initialMembers,
                        { userId: selectedUserToAdd, role: selectedRoleToAdd }
                      ])
                      setSelectedUserToAdd("")
                    }}
                    className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-9 px-3.5 flex items-center gap-1.5 cursor-pointer shrink-0 disabled:opacity-50"
                  >
                    <UserPlus className="size-3.5" />
                    <span>Adicionar</span>
                  </Button>
                </div>

                {/* Lista de Membros Adicionados */}
                {initialMembers.length > 0 && (
                  <div className="border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden divide-y divide-zinc-100 dark:divide-zinc-800 bg-white dark:bg-zinc-950">
                    {initialMembers.map((member) => {
                      const userObj = usersList.find((u) => u.id === member.userId)
                      const isCorpAdmin = (userObj as any)?.role === "owner" || (userObj as any)?.role === "admin"

                      return (
                        <div
                          key={member.userId}
                          className="flex items-center justify-between p-2.5 hover:bg-zinc-50/50 dark:hover:bg-zinc-900/50 transition-colors"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="size-7 rounded-full bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center text-xs font-bold text-zinc-700 dark:text-zinc-300 shrink-0">
                              {(userObj?.name || userObj?.email || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-zinc-900 dark:text-white truncate flex items-center gap-1.5">
                                {userObj?.name || userObj?.email}
                                {isCorpAdmin && (
                                  <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-1.5 py-0.2 rounded border border-blue-200 dark:border-blue-900/50">
                                    <ShieldCheck className="size-2.5" />
                                    Empresa
                                  </span>
                                )}
                              </p>
                              <p className="text-[10px] text-zinc-400 truncate">{userObj?.email}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            {isCorpAdmin ? (
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50/80 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900/50 rounded-lg px-2.5 py-1 select-none">
                                  Administrador
                                </span>
                                <span
                                  title="Administradores e donos da empresa têm acesso total e não podem ser removidos"
                                  className="p-1.5 text-zinc-300 dark:text-zinc-600 cursor-not-allowed"
                                >
                                  <ShieldCheck className="size-3.5 text-blue-500" />
                                </span>
                              </div>
                            ) : (
                              <>
                                <select
                                  value={member.role}
                                  onChange={(e) => {
                                    const newRole = e.target.value
                                    setInitialMembers((prev) =>
                                      prev.map((m) =>
                                        m.userId === member.userId ? { ...m, role: newRole } : m
                                      )
                                    )
                                  }}
                                  className="text-[11px] font-medium border border-zinc-200 dark:border-zinc-800 rounded-lg px-2 py-1 bg-zinc-50 dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 outline-none cursor-pointer hover:border-[#004EB1]"
                                >
                                  <option value="member">Membro</option>
                                  <option value="manager">Gerente</option>
                                  <option value="admin">Administrador</option>
                                </select>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setInitialMembers((prev) =>
                                      prev.filter((m) => m.userId !== member.userId)
                                    )
                                  }}
                                  title="Remover"
                                  className="p-1.5 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer"
                                >
                                  <Trash2 className="size-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100 dark:border-zinc-800">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowCreateWorkspaceModal(false)
                    setInitialMembers([])
                    setSelectedUserToAdd("")
                  }}
                  className="text-xs h-9 px-4 cursor-pointer"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={!newWsName.trim() || isSubmittingWorkspace}
                  className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-5 cursor-pointer shadow-sm"
                >
                  {isSubmittingWorkspace ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="size-3.5 animate-spin" />
                      Criando...
                    </span>
                  ) : (
                    "Criar Ambiente"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIGURAÇÃO / EDIÇÃO DO AMBIENTE */}
      {editingWorkspaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="w-full max-w-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden font-sans my-6 animate-in fade-in zoom-in-95 duration-150">
            {/* Cabeçalho */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-900/70">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-[#004EB1] border border-blue-100 dark:border-blue-900/40 flex items-center justify-center font-bold text-sm shadow-xs">
                  {editingWorkspaceModal.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold text-zinc-900 dark:text-white">
                      Configurar Ambiente
                    </h4>
                    <span className="text-[10px] bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 px-2 py-0.5 rounded-full font-mono border border-zinc-200 dark:border-zinc-700">
                      @{editingWorkspaceModal.slug}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-500">
                    Gerencie o nome de exibição e os membros com acesso a este ambiente
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditingWorkspaceModal(null)}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Conteúdo */}
            <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
              {/* Seção 1: Identificação e Nome */}
              <div className="p-4 bg-zinc-50/60 dark:bg-zinc-950/60 border border-zinc-200 dark:border-zinc-800 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <h5 className="text-xs font-bold text-zinc-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                    <Pencil className="size-3.5 text-[#004EB1]" />
                    Nome de Exibição
                  </h5>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-zinc-400 font-mono">Slug: {editingWorkspaceModal.slug}</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(editingWorkspaceModal.slug || "")
                        setCopiedSlug(true)
                        setTimeout(() => setCopiedSlug(false), 2000)
                        toast.success("Slug copiado!")
                      }}
                      title="Copiar Slug"
                      className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-200/50 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
                    >
                      {copiedSlug ? <Check className="size-3 text-emerald-500" /> : <Copy className="size-3" />}
                    </button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editWorkspaceNameInput}
                    onChange={(e) => setEditWorkspaceNameInput(e.target.value)}
                    placeholder="Nome do Ambiente"
                    className="flex-1 rounded-xl border border-zinc-200 dark:border-zinc-800 px-3.5 py-2 text-xs bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] outline-none transition-all"
                  />
                  <Button
                    type="button"
                    disabled={!editWorkspaceNameInput.trim() || editWorkspaceNameInput === editingWorkspaceModal.name}
                    onClick={async () => {
                      if (!handleUpdateWorkspace || !editingWorkspaceModal) return
                      try {
                        const updated = await handleUpdateWorkspace(editingWorkspaceModal.id, editWorkspaceNameInput.trim())
                        if (currentAccount) {
                          currentAccount.workspaces = (currentAccount.workspaces || []).map((w) =>
                            w.id === updated.id ? { ...w, name: updated.name } : w
                          )
                        }
                        setEditingWorkspaceModal({ ...editingWorkspaceModal, name: updated.name })
                        toast.success("Nome do ambiente atualizado!")
                      } catch (err: any) {
                        toast.error(err.message || "Erro ao atualizar nome.")
                      }
                    }}
                    className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-9 px-4.5 rounded-xl cursor-pointer shadow-xs disabled:opacity-40"
                  >
                    Salvar Nome
                  </Button>
                </div>
              </div>

              {/* Seção 2: Membros e Perfis do Ambiente */}
              <div className="space-y-3">
                <WorkspaceMembersManager
                  workspaceId={editingWorkspaceModal.id}
                  workspaceName={editingWorkspaceModal.name}
                  allUsers={usersList}
                />
              </div>
            </div>

            {/* Rodapé */}
            <div className="px-6 py-3.5 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50 flex justify-end">
              <Button
                type="button"
                onClick={() => setEditingWorkspaceModal(null)}
                className="bg-zinc-900 hover:bg-zinc-800 dark:bg-zinc-100 dark:hover:bg-zinc-200 text-white dark:text-zinc-900 text-xs font-semibold h-8.5 px-5 rounded-xl cursor-pointer transition-colors shadow-xs"
              >
                Concluir
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMAÇÃO DE ARQUIVAMENTO DE AMBIENTE */}
      {workspaceToArchive && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="size-11 rounded-full bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto shadow-xs">
              <Archive className="size-5" />
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white">
                Arquivar Ambiente
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                Tem certeza de que deseja arquivar o ambiente <strong>{workspaceToArchive.name}</strong>?
              </p>
              <div className="text-[11px] text-zinc-400 bg-zinc-50 dark:bg-zinc-800/50 p-2.5 rounded-xl text-left space-y-1 border border-zinc-100 dark:border-zinc-800">
                <p className="flex items-center gap-1.5 text-zinc-600 dark:text-zinc-300 font-medium">
                  <AlertTriangle className="size-3 text-amber-500 shrink-0" />
                  Os membros e preferências de layout serão desvinculados.
                </p>
                <p className="flex items-center gap-1.5 text-zinc-600 dark:text-zinc-300 font-medium">
                  <CheckCircle2 className="size-3 text-emerald-500 shrink-0" />
                  Aplicativos e widgets instalados serão preservados.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                disabled={isArchivingWorkspace}
                onClick={() => setWorkspaceToArchive(null)}
                className="text-xs h-8 font-sans cursor-pointer flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={isArchivingWorkspace}
                onClick={async () => {
                  if (!archiveAction || !workspaceToArchive) return
                  setIsArchivingWorkspace(true)
                  try {
                    await archiveAction(workspaceToArchive.id)
                    toast.success(`Ambiente "${workspaceToArchive.name}" arquivado com sucesso.`)
                    setWorkspaceToArchive(null)
                  } catch (err: any) {
                    toast.error(err.message || "Erro ao arquivar ambiente.")
                  } finally {
                    setIsArchivingWorkspace(false)
                  }
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold h-8 px-4 gap-1.5 cursor-pointer flex-1 shadow-xs"
              >
                {isArchivingWorkspace ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Arquivando...
                  </>
                ) : (
                  <>
                    <Archive className="size-3.5" />
                    Arquivar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMAÇÃO DE REATIVAÇÃO DE AMBIENTE */}
      {workspaceToReactivate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="size-11 rounded-full bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-xs">
              <ArchiveRestore className="size-5" />
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white">
                Reativar Ambiente
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                Deseja reativar o ambiente <strong>{workspaceToReactivate.name}</strong>?
              </p>
              <div className="text-[11px] text-zinc-400 bg-zinc-50 dark:bg-zinc-800/50 p-2.5 rounded-xl text-left space-y-1 border border-zinc-100 dark:border-zinc-800">
                <p className="flex items-center gap-1.5 text-zinc-600 dark:text-zinc-300 font-medium">
                  <CheckCircle2 className="size-3 text-emerald-500 shrink-0" />
                  O acesso será restaurado para administradores e proprietário.
                </p>
                <p className="flex items-center gap-1.5 text-zinc-600 dark:text-zinc-300 font-medium">
                  <CheckCircle2 className="size-3 text-emerald-500 shrink-0" />
                  Todos os apps e widgets instalados estarão disponíveis.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                disabled={isReactivatingWorkspace}
                onClick={() => setWorkspaceToReactivate(null)}
                className="text-xs h-8 font-sans cursor-pointer flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={isReactivatingWorkspace}
                onClick={async () => {
                  if (!reactivateAction || !workspaceToReactivate) return
                  setIsReactivatingWorkspace(true)
                  try {
                    await reactivateAction(workspaceToReactivate.id)
                    toast.success(`Ambiente "${workspaceToReactivate.name}" reativado com sucesso!`)
                    setWorkspaceToReactivate(null)
                  } catch (err: any) {
                    toast.error(err.message || "Erro ao reativar ambiente.")
                  } finally {
                    setIsReactivatingWorkspace(false)
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold h-8 px-4 gap-1.5 cursor-pointer flex-1 shadow-xs"
              >
                {isReactivatingWorkspace ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Reativando...
                  </>
                ) : (
                  <>
                    <ArchiveRestore className="size-3.5" />
                    Reativar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </TabsContent>
  )
}
